import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { Input, Select as AntSelect, Spin } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle'
import { CropImagePreview } from 'components/reusables/cropImagePreview'
import Select from 'components/reusables/select'
import LearningPathProfilesField from 'components/reusables/learningPathProfilesField'
import { ProfileSelectOption } from 'components/ui/forms/course/components/forms/RequiredProfilesInput'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { slices } from 'store/slices'
import { LearningPathStatus } from 'enum/learningPathStatus.enum'
import { learningPathsService } from 'api/administration/learningPaths'
import { LearningPathStateBadge } from 'components/reusables/states'
import { StateAlert } from './styled'
import { Title, Subtitle } from './../styled'

const { Option } = AntSelect

const Configuration = () => {
  const { t } = useTranslation()
  const prefix = 'administration.learningPaths.manage.create.configuration'
  const dispatch = useAppDispatch()
  const params = useParams<{ id: string }>()
  const [showStateAlert, setShowStateAlert] = useState(false)
  const profiles = useAppSelector(slices.profiles.items)
  const loadingProfiles = useAppSelector(slices.profiles.loading)
  const loadedProfiles = useAppSelector(slices.profiles.loaded)
  const configuration = useAppSelector(slices.learningPath.configuration)
  const loadingConfiguration = useAppSelector(
    slices.learningPath.loadingConfiguration
  )

  useEffect(() => {
    if (!loadedProfiles && !loadingProfiles) getProfiles()
  }, [])

  const getProfiles = async () => {
    dispatch(slices.profiles.setLoading(true))
    await learningPathsService.getProfiles().then((response) => {
      dispatch(slices.profiles.setItems(response))
    })
    dispatch(slices.profiles.setLoading(false))
  }

  const onChangeState = (value: number) => {
    dispatch(slices.learningPath.setState(value))
    setShowStateAlert(value === LearningPathStatus.draft)
  }

  const selectedProfiles = useMemo<ProfileSelectOption[]>(() => {
    const profileById = new Map(
      profiles.map((profile) => [Number(profile.id), profile])
    )

    return configuration.profiles
      .map((profileId) => profileById.get(Number(profileId)))
      .filter(Boolean)
      .map((profile) => ({
        value: Number(profile!.id),
        label: profile!.name,
        color: profile!.color,
      }))
  }, [configuration.profiles, profiles])

  const learningPathId = configuration.id || undefined

  return (
    <Spin spinning={loadingConfiguration}>
      <Title>{t(`${prefix}.title`)}</Title>
      <Subtitle $required>{t(`${prefix}.inputs.name.title`)}</Subtitle>
      <Input
        value={configuration.name}
        onChange={(event) =>
          dispatch(slices.learningPath.setName(event.target.value))
        }
        autoFocus
      />
      <Subtitle>{t(`${prefix}.inputs.description.title`)}</Subtitle>
      <Input.TextArea
        value={configuration.description}
        onChange={(event) =>
          dispatch(slices.learningPath.setDescription(event.target.value))
        }
        autoSize={{ minRows: 2, maxRows: 2 }}
      />
      <Subtitle>{t(`${prefix}.inputs.image.title`)}</Subtitle>
      <CropImagePreview
        name="image"
        image={configuration.image}
        setImage={(_input: string, value: any) =>
          dispatch(slices.learningPath.setImage(value))
        }
      />
      <Subtitle>{t(`${prefix}.inputs.profiles.title`)}</Subtitle>
      <Spin spinning={loadingProfiles}>
        <LearningPathProfilesField
          profiles={selectedProfiles}
          learningPathId={learningPathId}
          onChange={(nextProfiles) =>
            dispatch(
              slices.learningPath.setProfiles(
                nextProfiles.map((profile) => Number(profile.value))
              )
            )
          }
        />
      </Spin>
      {params.id && (
        <>
          <Subtitle>{t(`${prefix}.inputs.state.title`)}</Subtitle>
          <Select
            value={configuration.state}
            onSelect={(value) => onChangeState(value)}
          >
            <Option value={LearningPathStatus.draft}>
              <LearningPathStateBadge state={LearningPathStatus.draft} />
            </Option>
            <Option value={LearningPathStatus.published}>
              <LearningPathStateBadge state={LearningPathStatus.published} />
            </Option>
          </Select>
          {showStateAlert && (
            <StateAlert>
              <FontAwesomeIcon icon={faInfoCircle as IconProp} />
              {t(`${prefix}.inputs.state.alert`)}
            </StateAlert>
          )}
        </>
      )}
    </Spin>
  )
}

export default Configuration
